import { tr } from '../lib/i18nHelpers';

/**
 * Provenance badge for an invoice number (design 2026-10-12, UI merged
 * 2026-10-16): how the stored number was obtained — printed (parser read it
 * off the document), recovered (salvage found it in the OCR header after a
 * parse miss/glue), generated (system placeholder), manual (hand-keyed).
 * printed and recovered share one AI-OCR pill in the UI; the DB keeps all
 * four tiers distinct (invoice_number_source) for system checks and
 * debugging, and the tooltip stays tier-specific so the recovered
 * "double-check" nudge survives on hover. Historical rows have NULL source
 * → no badge (unknown).
 */

const VARIANTS: Record<string, { label: [string, string, string]; tip: [string, string, string]; className: string }> = {
  printed: {
    label: ['AI-OCR', 'AI-OCR', 'AI-OCR'],
    tip: ['Number read from the document', '號碼讀取自文件', '号码读取自文件'],
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  recovered: {
    label: ['AI-OCR', 'AI-OCR', 'AI-OCR'],
    tip: ['Number recovered from the document header — double-check it', '號碼從文件頁首尋回 — 請覆核', '号码从文件页首寻回 — 请复核'],
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  generated: {
    label: ['Auto', '自動', '自动'],
    tip: ['System-generated placeholder — not printed on the document', '系統自動產生 — 並非文件上的號碼', '系统自动产生 — 并非文件上的号码'],
    className: 'bg-gray-100 text-gray-900 dark:text-gray-200 border-gray-200',
  },
  manual: {
    label: ['Manual', '手輸', '手输'],
    tip: ['Number entered by hand', '號碼由人手輸入', '号码由人手输入'],
    className: 'bg-slate-100 text-slate-600 border-slate-300',
  },
};

export default function InvoiceNumberBadge({
  source,
  direction,
  counterpartyRef,
}: {
  source?: string | null;
  direction?: string | null;
  counterpartyRef?: string | null;
}) {
  const v = source ? VARIANTS[source] : null;
  if (!v) return null;
  // A generated PURCHASE number hides the supplier's printed number — surface it.
  const supplierTip = source === 'generated' && direction === 'incoming' && counterpartyRef
    ? tr(`Supplier's no.: ${counterpartyRef}`, `供應商編號：${counterpartyRef}`, `供应商编号：${counterpartyRef}`)
    : null;
  return (
    <span
      title={supplierTip ? `${tr(...v.tip)} · ${supplierTip}` : tr(...v.tip)}
      className={`inline-flex shrink-0 items-center rounded border px-1 py-px text-[10px] font-medium leading-none ${v.className}`}
    >
      {tr(...v.label)}
    </span>
  );
}
